// src/app/auth.service.ts
import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
  HttpResponse,
  HttpResponseBase,
} from "@angular/common/http";
import { BehaviorSubject, Observable, Subscriber } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { Messages } from "./model/messages";
import { Access } from "./model/access";
import { LoggedIn } from "./model/loggedIn";
import { EnvironmentHelper } from "./services/env.helper";
import { CookieService } from "ngx-cookie-service";
import { NavigationHelper } from "./services/navigation.helper";
@Injectable({
  providedIn: "root",
})
export class AuthService {
  private apiUrl: string;
  private keyCurrentAccess = "currentAccess";
  private keyCurrentLoggedIn = "currentLoggedIn";
  private currentUser: any = null;
  private _currentAccess: BehaviorSubject<Access>;
  public _currentLoggedIn: BehaviorSubject<LoggedIn>;

  private translator: TranslateService;
  constructor(
    private http: HttpClient,
    private env: EnvironmentHelper,
    private cookieService: CookieService,
    private navigationHelper: NavigationHelper
  ) {
    this.apiUrl = env.apiBaseUrl + "login/";
    this._currentAccess = new BehaviorSubject<Access>(
      new Access("", 0, new Date())
    );
    this.readAccessFromLocalStorage();
    this._currentLoggedIn = new BehaviorSubject(
      new LoggedIn(0, "", "", "", false, "")
    );
    this.readUserDataFromLocalStorage();
  }

  private readUserDataFromLocalStorage() {
    let existingLoggedIn = this._currentLoggedIn.value;
    const existingItem = localStorage.getItem(this.keyCurrentLoggedIn);
    if (existingItem) {
      try {
        existingLoggedIn = JSON.parse(existingItem);
        if (existingLoggedIn) {
          this._currentLoggedIn.next(existingLoggedIn);
        }
      } catch (e) {}
    }
  }
  private readAccessFromLocalStorage() {
    let existingAccess = this._currentAccess.value;
    const existingItem = localStorage.getItem(this.keyCurrentAccess);
    if (existingItem) {
      existingAccess = JSON.parse(existingItem);
      if (existingAccess) {
        this._currentAccess.next(existingAccess);
      }
    }
  }

  public login(username: string, password: string): Observable<Messages> {
    return new Observable<Messages>((observer) => {
      const myheader = new HttpHeaders().set(
        "Content-Type",
        "application/x-www-form-urlencoded"
      );

      let body = new HttpParams();

      body = body.set("username", username);
      body = body.set("password", password);

      this.http
        .post<any>(this.apiUrl, body, {
          headers: myheader,
          observe: "response",
        })
        .subscribe(
          (response) => {
            this.receiveLogin(response, observer);
          },
          (error) => {
            this.receiveLogin(error, observer);
          }
        );
    });
  }
  private receiveLogin(
    response: HttpResponseBase,
    observer: Subscriber<Messages>
  ) {
    if (response.status === 200) {
      const data = (response as HttpResponse<any>).body;
      const token = data?.accessToken;
      if (!token) {
        observer.next(Messages.from({ account: ["login.failed"] }));
        return;
      }
      this._currentAccess.next(data);
      localStorage.setItem(
        this.keyCurrentAccess,
        JSON.stringify(this._currentAccess.value)
      );

      this.retrieveLogin(this.token).subscribe((messages) => {
        observer.next(messages);
      });
    } else if (response.status === 401) {
      const reloaded = Messages.from((response as HttpErrorResponse).error);
      observer.next(reloaded);
    } else {
      observer.next(Messages.from({ account: ["login.not.ok"] }));
    }
  }

  public retrieveLogin(token: string): Observable<Messages> {
    const self = this;
    return new Observable<Messages>((observer) => {
      this.http
        .get<any>(this.apiUrl, {
          headers: { Authorization: "Bearer " + token },
          observe: "response",
        })
        .subscribe(
          (response) => {
            this.receiveUser(response, observer);
          },
          (error) => {
            this.receiveUser(error, observer);
          }
        );
    });
  }
  private receiveUser(
    response: HttpResponseBase,
    observer: Subscriber<Messages>
  ) {
    if (response.status === 200) {
      const data = (response as HttpResponse<LoggedIn>)?.body;

      if (!data?.username) {
        observer.next(Messages.from({ account: ["login.unknown"] }));
        return;
      }

      this._currentLoggedIn.next(data!);
      localStorage.setItem(this.keyCurrentLoggedIn, JSON.stringify(data));
      observer.next(Messages.EMPTY);
    } else if (response.status === 401) {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "account",
          "login.not.authorized"
        )
      );
    } else {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "account",
          "login.incomplete"
        )
      );
    }
  }

  logout(): void {
    localStorage.removeItem("authToken");
    localStorage.removeItem(this.keyCurrentAccess);
    localStorage.removeItem(this.keyCurrentLoggedIn);

    this.currentUser = null;
    this._currentAccess.next(new Access("", 0, new Date()));
    this._currentLoggedIn.next(new LoggedIn(0, "", "", "", false, ""));
    this.navigationHelper.clearNavigationState();
  }
  public isSupervisor(): boolean {
    const loggedInRaw = localStorage.getItem(this.keyCurrentLoggedIn);

    if (loggedInRaw) {
      try {
        const loggedInObj = JSON.parse(loggedInRaw);
        return (
          loggedInObj?.supervisor === true || loggedInObj?.supervisor === "true"
        );
      } catch (e) {
        return false;
      }
    }

    return false;
  }

  isAuthenticated(): boolean {
    return !!this._currentAccess.value.accessToken;
  }
  get currentLoggedIn(): LoggedIn {
    return this._currentLoggedIn.value;
  }
  public get currentAccess(): Observable<Access> {
    return this._currentAccess;
  }

  public get currentUserData(): Observable<LoggedIn> {
    return this.currentUser;
  }

  public get token(): string {
    return this._currentAccess.value.accessToken;
  }

  public get userdata(): LoggedIn {
    const raw = this._currentLoggedIn.value;

    return new LoggedIn(
      raw.id ?? 0,
      raw.username,
      raw.label,
      raw.email,
      raw.supervisor,
      raw.lang
    );
  }
}
